import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { List, Map } from 'immutable';
import { merge, Observable, of, Subject } from 'rxjs';
import { Account } from '../../../account-data/account.model';
import { AttachmentView } from '../../../attachments/attachment-view.model';
import { AttachmentBuilder } from '../../../attachments/attachment.builder';
import { AttachmentsService } from '../../../attachments/attachments.service';
import { AttachmentsStore } from '../../../attachments/attachments.store';
import { DialogService } from '../../../dialog/dialog.service';
import {
  ADD_APPOINTMENT_QUERY_PARAM,
  ADD_TRACKED_TIME_QUERY_PARAM,
  DocumentTypesEnum,
  IEditTaxRateProperties,
} from '../../../document-data/document-data.data';
import { Document } from '../../../document-data/document.model';
import { Billing } from '../../../document-data/sub-models/billing.model';
import { CalculationResult } from '../../../document-data/sub-models/calculation-result.model';
import { Deposit } from '../../../document-data/sub-models/deposit.model';
import { Discount } from '../../../document-data/sub-models/discount.model';
import { DocumentItem } from '../../../document-data/sub-models/document-item.model';
import { Schedule } from '../../../document-data/sub-models/schedule.model';
import { Shipping } from '../../../document-data/sub-models/shipping.model';
import { States } from '../../../document-data/sub-models/states.model';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { NavigationService } from '../../../navigation/navigation.service';
import { SettingsDataStore } from '../../../settings-data/settings-data.store';
import { Settings } from '../../../settings-data/settings.model';
import { CompanySettings } from '../../../settings-data/sub-models/company-settings/company-settings.model';
import { PaymentsSettings } from '../../../settings-data/sub-models/company-settings/payments.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../../../settings-data/sub-models/company-settings/withholding-tax.model';
import { CustomField } from '../../../settings-data/sub-models/custom-field.model';
import { DocumentNotes } from '../../../settings-data/sub-models/document-preset-settings/document-notes.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { DateTimeService } from 'i2g-ng-utils';
import { UtilsService } from '../../../shared/services/utils/utils.service';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingObject,
  ITrackingProperties,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { isUndefined } from 'lodash';
import { DepositToggleValue } from '../../../settings-data/sub-models/company-settings/deposit-toggle-value.model';
import { SettingsDataDispatcher } from '../../../settings-data/settings-data.dispatcher';
import {
  distinctUntilChanged,
  map,
  mergeMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { InvoiceDataStore } from '../../../invoice-data/invoice-data.store';
import { IDocumentStore } from '../../../document-data/document-store.interface';
import { IDocumentDataDispatcher } from '../../../document-data/document-data-dispatcher.interface';
import { IDocumentDataBuilder } from '../../../document-data/document-data-builder.interface';
import { INVOICES_MASTER_ROUTE } from '../../../main/main.data';
import { ItemSaveService } from '../../../shared/services/item-save/item-save.service';

@Component({
  selector: 'app-document-create',
  templateUrl: './document-create.component.html',
  styleUrls: ['./document-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [AttachmentsService, AttachmentsStore, AttachmentBuilder],
})
export class DocumentCreateComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Input() documentDataDispatcher: IDocumentDataDispatcher;
  @Input() documentDataStore: IDocumentStore;
  @Input() documentDataBuilder: IDocumentDataBuilder;
  @Input() showDueDate: boolean;
  @Input() showPaid: boolean;
  @Input() useOutstandingBalance: boolean;
  @Input() showTotal: boolean;
  @Input() enableDiscount: boolean;
  @Input() enableShipping: boolean;
  @Input() enablePaymentDetails: boolean;
  @Input() enableDeposits: boolean;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() featureToggles: Toggles;
  @Input() featurePayloads: Map<string, any>;
  @Input() depositRequestFeatureName: string;
  @Input() account: Account;

  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _activeItemReferenceId: Subject<string> = new Subject<string>();

  attachments$: Observable<List<AttachmentView>>;
  documentAdditionalFields$: Observable<List<CustomField>>;
  documentBilling$: Observable<Billing>;
  documentCalculationResult$: Observable<CalculationResult>;
  documentDateTerms$: Observable<{ docDate: string; terms: number }>;
  documentDiscount$: Observable<Discount>;
  documentId$: Observable<string>;
  documentItems$: Observable<List<DocumentItem>>;
  documentNotes$: Observable<DocumentNotes>;
  documentNumber$: Observable<string>;
  documentOverallState$: Observable<string>;
  documentPaymentDetails$: Observable<string>;
  documentSettings$: Observable<DocumentPresetSettings>;
  companySettings$: Observable<CompanySettings>;
  documentShipping$: Observable<Shipping>;
  documentState$: Observable<States>;
  documentTaxes$: Observable<List<Tax>>;
  documentTrackingObject$: Observable<ITrackingObject>;
  documentSchedule$: Observable<Schedule>;
  documentType$: Observable<DocumentTypesEnum>;
  documentWithholdingTax$: Observable<WithholdingTax>;
  accountSettings$: Observable<Settings>;
  activeItemReferenceId$: Observable<string>;
  deposit$: Observable<Deposit>;
  addDepositForFuture$: Observable<DepositToggleValue>;
  documentDueDate$: Observable<string>;
  paymentsSettings$: Observable<PaymentsSettings>;
  document$: Observable<Document>;
  trackingAdditionalProperties: ITrackingProperties;

  constructor(
    private _route: ActivatedRoute,
    private _settingsDataDispatcher: SettingsDataDispatcher,
    private _trackingDispatcher: TrackingDispatcher,
    private _attachmentsStore: AttachmentsStore,
    private _settingsDataStore: SettingsDataStore,
    private _dateTimeService: DateTimeService,
    private _navigationService: NavigationService,
    private _dialogService: DialogService,
    private _utilsService: UtilsService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _itemSaveService: ItemSaveService
  ) {}

  ngOnInit() {
    const hasItemSaveAccess = this._itemSaveService.hasItemSaveV1Enabled(
      this.featurePayloads
    );
    this.trackingAdditionalProperties = hasItemSaveAccess
      ? this._itemSaveService.getVariantKeyTracking(this.featurePayloads)
      : {};

    const validDocument$: Observable<Document> = this.documentDataStore.getValidEditingEntity();

    this.documentSchedule$ =
      this.documentDataStore instanceof InvoiceDataStore
        ? validDocument$.pipe(
            mergeMap((document: Document) =>
              (this
                .documentDataStore as InvoiceDataStore).getSchedulesByInvoiceId(
                document.id
              )
            ),
            distinctUntilChanged((a, b) => a && a.equals(b))
          )
        : of(null);

    this.document$ = validDocument$;

    this.activeItemReferenceId$ = merge(
      this._route.queryParamMap.pipe(
        takeUntil(this._destroyed),
        take(1),
        map(
          (paramMap: ParamMap) =>
            paramMap.get(ADD_TRACKED_TIME_QUERY_PARAM) ||
            paramMap.get(ADD_APPOINTMENT_QUERY_PARAM)
        )
      ),
      this._activeItemReferenceId.asObservable().pipe(distinctUntilChanged())
    );

    this.documentId$ = validDocument$.pipe(
      map((doc: Document) => doc.id),
      take(1)
    );

    this.documentType$ = validDocument$.pipe(
      map((doc: Document) => doc.documentType),
      take(1)
    );

    this.documentDueDate$ = validDocument$.pipe(
      map((doc: Document) => doc.dueDate),
      distinctUntilChanged()
    );

    this.documentSettings$ = validDocument$.pipe(
      map((doc: Document) => doc.settings),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentState$ = validDocument$.pipe(
      map((doc: Document) => doc.states),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentShipping$ = validDocument$.pipe(
      map((doc: Document) => doc.shipping),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentItems$ = validDocument$.pipe(
      map((document: Document) => document.documentItems),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentBilling$ = validDocument$.pipe(
      map((document: Document) => document.billing),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentAdditionalFields$ = validDocument$.pipe(
      map((document: Document) => document.settings.customFields),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentDiscount$ = validDocument$.pipe(
      map((document: Document) => document.discount),
      distinctUntilChanged((a, b) => a && a.equals(b)),
      map(
        (discount: Discount) =>
          discount || this.documentDataBuilder.buildDiscount()
      )
    );

    this.documentOverallState$ = validDocument$.pipe(
      map((doc: Document) => doc.overallState),
      distinctUntilChanged()
    );

    this.documentTaxes$ = validDocument$.pipe(
      map((doc: Document) => doc.taxes),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentCalculationResult$ = validDocument$.pipe(
      map((doc: Document) => doc.calculationResult),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentWithholdingTax$ = validDocument$.pipe(
      map((doc: Document) => doc.withholdingTax),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentNotes$ = validDocument$.pipe(
      map((document: Document) => document.settings.notes),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentNumber$ = validDocument$.pipe(
      map((doc: Document) => doc.docNumber),
      distinctUntilChanged()
    );

    this.documentPaymentDetails$ = validDocument$.pipe(
      map((document: Document) => document.settings.paymentDetails),
      distinctUntilChanged()
    );

    this.documentDateTerms$ = validDocument$.pipe(
      distinctUntilChanged(
        (a, b) =>
          this._dateTimeService.isSameDate(a.docDate, b.docDate) &&
          a.terms === b.terms
      ),
      map((doc: Document) => ({ docDate: doc.docDate, terms: doc.terms }))
    );

    this.attachments$ = this._attachmentsStore.files$;

    this.documentTrackingObject$ = validDocument$.pipe(
      map((doc: Document) =>
        this._trackingObjectBuilder.buildDocument(
          doc.id,
          doc.localId,
          doc.docNumber,
          doc.overallState,
          doc.revisionId,
          doc.documentType
        )
      )
    );

    this.accountSettings$ = this._settingsDataStore.settings$.pipe(
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.documentSettings$ = validDocument$.pipe(
      map((doc: Document) => doc.settings),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.deposit$ = validDocument$.pipe(
      map((doc: Document) => doc.deposit),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.addDepositForFuture$ = this.accountSettings$.pipe(
      withLatestFrom(this.documentType$),
      map(([settings, documentType]: [Settings, DocumentTypesEnum]) => {
        const previousValue =
          settings.companySettings.depositToggle[documentType];

        if (previousValue) {
          return previousValue;
        } else {
          const depositToggleValueKeys = DepositToggleValue.KEYS;
          const depositTogglePayload =
            this.featurePayloads &&
            this.featurePayloads.get(Toggles.KEYS.DEPOSIT_TOGGLE);
          const documentToggleValue =
            depositTogglePayload && depositTogglePayload.get(documentType);

          return !isUndefined(documentToggleValue)
            ? new DepositToggleValue({
                [depositToggleValueKeys.VALUE]: 50,
                [depositToggleValueKeys.TOGGLE_STATE]: documentToggleValue,
              })
            : null;
        }
      }),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.companySettings$ = this._settingsDataStore.settings$.pipe(
      map((settings: Settings) => settings.companySettings),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

    this.paymentsSettings$ = this.companySettings$.pipe(
      map((companySettings: CompanySettings) => companySettings.payments),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
  }

  ngAfterViewInit() {
    this._trackingDispatcher.trackPage(
      this.trackingScreenName,
      this.trackingAdditionalProperties
    );
  }

  updateDocumentContent(documentContentProps: Map<string, any>) {
    this.documentDataDispatcher.setEditingEntityContent(documentContentProps);
  }

  updateDocument(documentProps: Map<string, any>) {
    this.documentDataDispatcher.setEditingEntityProperties(documentProps);
  }

  updateSettingsContent(settingsContentProps: Map<string, any>) {
    this._settingsDataDispatcher.updateSettings(settingsContentProps);
    this._settingsDataDispatcher.saveCurrentSettingsToNetwork();
  }

  updateDocumentTaxRates(taxRateProps: IEditTaxRateProperties) {
    this.documentDataDispatcher.updateTaxInEditing(taxRateProps);
  }

  setInTmpCache() {
    this.document$
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe((editingDocument: Document) => {
        this.documentDataDispatcher.setInTmpCache(
          editingDocument.id,
          editingDocument
        );
      });
  }

  activeItemEditMode(referenceId: string) {
    this._activeItemReferenceId.next(referenceId);
  }

  navigateToConvertedDoc() {
    this.document$
      .pipe(take(1), takeUntil(this._destroyed))
      .subscribe((document: Document) => {
        this._navigationService.goTo([
          INVOICES_MASTER_ROUTE,
          this._utilsService.getConvertedDoc(document).id,
        ]);
      });
  }

  ngOnDestroy() {
    this._dialogService.closeLoadingDialog();

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
